import type { AudioSettings, MediaAsset, Project } from '@cinegrid/core';
import { defaultAudioSettings } from '@cinegrid/core';
import { createAudioFxChain, resolveAudioSettings } from '../engine/audioFx';
import { resolveAssetUrl } from '../media/mediaStore';

export type MixedAudio = {
  sampleRate: number;
  numberOfChannels: 2;
  /** Planar channels: [left, right] */
  channels: [Float32Array, Float32Array];
  length: number;
  duration: number;
};

type ActiveClip = {
  start: number;
  duration: number;
  inPoint: number;
  gain: number;
  audio: AudioSettings;
  asset: MediaAsset;
};

/**
 * Collect timeline clips that contribute audio (mirrors previewAudio rules).
 */
export function collectAudioClips(project: Project, rangeStart: number, rangeEnd: number): ActiveClip[] {
  const anySolo = project.tracks.some((t) => t.solo);

  const audioAssetIds = new Set<string>();
  for (const clip of project.clips) {
    if (clip.kind !== 'audio' || !clip.assetId || clip.muted) continue;
    if (clip.start + clip.duration <= rangeStart || clip.start >= rangeEnd) continue;
    const track = project.tracks.find((t) => t.id === clip.trackId);
    if (!track || track.muted) continue;
    if (anySolo && !track.solo) continue;
    audioAssetIds.add(clip.assetId);
  }

  const out: ActiveClip[] = [];

  for (const track of project.tracks) {
    if (track.muted) continue;
    if (anySolo && !track.solo) continue;

    for (const clip of project.clips) {
      if (clip.trackId !== track.id) continue;
      if (clip.muted || !clip.assetId) continue;
      if (clip.kind !== 'video' && clip.kind !== 'audio') continue;
      if (clip.start + clip.duration <= rangeStart || clip.start >= rangeEnd) continue;

      if (clip.kind === 'video' && audioAssetIds.has(clip.assetId)) {
        continue;
      }

      const asset = project.assets.find((a) => a.id === clip.assetId);
      if (!asset || asset.kind === 'image' || asset.kind === 'adjustment' || asset.kind === 'solid') {
        continue;
      }

      out.push({
        start: clip.start,
        duration: clip.duration,
        inPoint: clip.inPoint,
        gain: Math.max(0, Math.min(1, clip.properties.transform.opacity ?? 1)),
        audio: resolveAudioSettings(clip.properties.audio ?? defaultAudioSettings()),
        asset,
      });
    }
  }

  return out;
}

async function decodeAssetBuffer(
  asset: MediaAsset,
  cache: Map<string, AudioBuffer>,
  ctx: BaseAudioContext
): Promise<AudioBuffer | null> {
  const cached = cache.get(asset.id);
  if (cached) return cached;
  try {
    const url = await resolveAssetUrl(asset);
    const res = await fetch(url);
    const raw = await res.arrayBuffer();
    const buf = await ctx.decodeAudioData(raw.slice(0));
    cache.set(asset.id, buf);
    return buf;
  } catch (err) {
    console.warn('Export audio decode failed', asset.name, err);
    return null;
  }
}

async function renderClipWithFx(
  buffer: AudioBuffer,
  clip: ActiveClip,
  rangeStart: number,
  rangeEnd: number,
  sampleRate: number
): Promise<AudioBuffer | null> {
  const overlapStart = Math.max(clip.start, rangeStart);
  const overlapEnd = Math.min(clip.start + clip.duration, rangeEnd);
  if (overlapEnd <= overlapStart) return null;

  const outDur = rangeEnd - rangeStart;
  const reverbTail = clip.audio.reverb.enabled ? 0.4 + clip.audio.reverb.size * 2.6 : 0.05;
  const frames = Math.max(1, Math.ceil((outDur + reverbTail) * sampleRate));
  const ctx = new OfflineAudioContext(2, frames, sampleRate);

  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const fx = createAudioFxChain(ctx);
  fx.apply({
    ...clip.audio,
    volume: clip.audio.volume * clip.gain,
  });
  src.connect(fx.input);
  fx.output.connect(ctx.destination);

  const when = overlapStart - rangeStart;
  const offset = clip.inPoint + (overlapStart - clip.start);
  const dur = overlapEnd - overlapStart;
  try {
    src.start(Math.max(0, when), Math.max(0, offset), dur);
  } catch {
    return null;
  }

  return ctx.startRendering();
}

/**
 * Offline-mix timeline audio for [rangeStart, rangeEnd) into stereo float PCM.
 * Applies per-clip volume / NR / EQ / compressor / reverb.
 */
export async function mixTimelineAudio(
  project: Project,
  rangeStart: number,
  rangeEnd: number,
  sampleRate = 48_000
): Promise<MixedAudio | null> {
  const duration = Math.max(0, rangeEnd - rangeStart);
  if (duration <= 0) return null;

  const clips = collectAudioClips(project, rangeStart, rangeEnd);
  if (clips.length === 0) return null;

  const OfflineCtx =
    window.OfflineAudioContext ||
    (window as unknown as { webkitOfflineAudioContext: typeof OfflineAudioContext })
      .webkitOfflineAudioContext;
  const decodeCtx = new OfflineCtx(1, 1, sampleRate);
  const cache = new Map<string, AudioBuffer>();

  const length = Math.max(1, Math.ceil(duration * sampleRate));
  const left = new Float32Array(length);
  const right = new Float32Array(length);

  for (const clip of clips) {
    const buffer = await decodeAssetBuffer(clip.asset, cache, decodeCtx);
    if (!buffer) continue;
    const rendered = await renderClipWithFx(buffer, clip, rangeStart, rangeEnd, sampleRate);
    if (!rendered) continue;
    const n = Math.min(length, rendered.length);
    const rl = rendered.getChannelData(0);
    const rr =
      rendered.numberOfChannels > 1 ? rendered.getChannelData(1) : rl;
    for (let i = 0; i < n; i++) {
      left[i] += rl[i] ?? 0;
      right[i] += rr[i] ?? 0;
    }
  }

  for (let i = 0; i < length; i++) {
    left[i] = Math.tanh(left[i]);
    right[i] = Math.tanh(right[i]);
  }

  return {
    sampleRate,
    numberOfChannels: 2,
    channels: [left, right],
    length,
    duration: length / sampleRate,
  };
}

/** Convert mixed audio to an AudioBuffer for MediaStream playback. */
export function mixedToAudioBuffer(mixed: MixedAudio): AudioBuffer {
  const ctx = new OfflineAudioContext(2, mixed.length, mixed.sampleRate);
  const buf = ctx.createBuffer(2, mixed.length, mixed.sampleRate);
  buf.getChannelData(0).set(mixed.channels[0]);
  buf.getChannelData(1).set(mixed.channels[1]);
  return buf;
}
